/**
 * scoreadmin-fe permission namespace + constants (AUTHZ.md §5.4), card #2415.
 *
 * Follows the score-fe/src/permissions.js precedent: derive role/permission
 * checks from score-common's manifest (score-common/permissions) rather than
 * duplicating a literal, so a future role grant is picked up automatically.
 * scoreadmin-fe's own ScoreEntitlementGate.jsx already reads the access
 * token's `roles` claim directly instead of common-fe's usePermission() /
 * PermissionProvider, for the same reason score-fe's header explains:
 * account-api's pre-token-generation.js emits no `permissions` claim at all
 * (#276 never landed), so usePermission() fails closed for every real,
 * non-super user. hasAssignScribesPermission below matches that pattern.
 */
import { manifest } from "score-common/permissions";

export const PERMS = Object.freeze({
  ASSIGN_SCRIBES: "assign_scribes",
  // #2862 — the placed-class lock's admin-only override (change placings on
  // a class whose placings are already set). Same manifest-derived pattern
  // as assign_scribes below, never an inline role string.
  OVERRIDE_PLACED_CLASS: "override_placed_class",
  // #2816 — switch which of two contender sheets for a judge seat is
  // authoritative. Same manifest-derived pattern, never an inline role
  // string. AC4: a scribe holds neither this nor assign_scribes, so a
  // scribe-only token never even shows the switch button.
  SWITCH_AUTHORITATIVE_SCORE_SHEET: "switch_authoritative_score_sheet",
  // #3009 — resolve a same-field stale_write conflict (keep_current or
  // apply_attempted) raised on the review queue. Same manifest-derived
  // pattern, score_admin only (never score_scribe, never score_judge —
  // decided 2026-09-20: the tent resolves it, never the scribe who typed the
  // value). Drives whether ReviewQueue.jsx shows the two resolve buttons on
  // a stale_write row at all.
  RESOLVE_STALE_WRITE_CONFLICT: "resolve_stale_write_conflict",
});

// Roles whose manifest permissions include a given short key — derived from
// the manifest, not hardcoded, so a future grant to another role is picked up
// automatically.
function rolesWithPermission(permissionKey) {
  return Object.freeze(
    Object.entries(manifest.roles)
      .filter(([, role]) => role.permissions.includes(permissionKey))
      .map(([key]) => key)
  );
}

// Roles holding assign_scribes — currently just score_admin
// (packages/score-common/src/permissions/index.js).
export const ASSIGN_SCRIBES_ROLES = rolesWithPermission(PERMS.ASSIGN_SCRIBES);

// #2862 — roles holding override_placed_class (currently just score_admin).
export const OVERRIDE_PLACED_CLASS_ROLES = rolesWithPermission(PERMS.OVERRIDE_PLACED_CLASS);

// #2816 — roles holding switch_authoritative_score_sheet (currently just
// score_admin — see packages/score-common/src/permissions/index.js).
export const SWITCH_AUTHORITATIVE_SCORE_SHEET_ROLES = rolesWithPermission(
  PERMS.SWITCH_AUTHORITATIVE_SCORE_SHEET
);

// #3009 — roles holding resolve_stale_write_conflict (currently just
// score_admin — see packages/score-common/src/permissions/index.js).
export const RESOLVE_STALE_WRITE_CONFLICT_ROLES = rolesWithPermission(
  PERMS.RESOLVE_STALE_WRITE_CONFLICT
);

/**
 * Pure predicate: does this user hold score:assign_scribes for the active
 * org? `rolesForOrg` is the decoded token's `roles[activeOrgId]` array
 * (qualified "score:score_admin" or bare "score_admin" — same two forms
 * ScoreEntitlementGate.jsx's activeOrgHasScoreAdminRole matches).
 *
 * Deliberately does NOT special-case a super user: score-api's route
 * (setClassPaperEntry, requirePermission('assign_scribes')) bypasses for
 * super, but every other per-field control on this screen (walk-trot-only,
 * DSHB override, ...) has no FE-side permission gate at all — this is the
 * first one card #2415 adds — and a genuine super admin who needs this can
 * hold score_admin for the org like anyone else, or call the route
 * directly. Keeping the FE check to the literal roles claim (no isSuper
 * bypass) keeps it simple and, unlike the app-shell gate, testable without
 * a super-only code path.
 */
function holdsAnyRole(rolesForOrg, allowedRoles) {
  const roles = Array.isArray(rolesForOrg) ? rolesForOrg : [];
  return roles.some(
    (r) => allowedRoles.includes(r) || allowedRoles.includes(r?.split(":").pop())
  );
}

export function hasAssignScribesPermission(rolesForOrg) {
  return holdsAnyRole(rolesForOrg, ASSIGN_SCRIBES_ROLES);
}

/**
 * #2862 — does this user hold score:override_placed_class for the active org
 * (i.e. may they change placings on a class whose placings are already set)?
 * Same literal-roles-claim predicate as hasAssignScribesPermission above —
 * no isSuper bypass on the FE (a super who needs it holds score_admin, or the
 * API's own super bypass covers a direct call). Drives whether the Placings
 * screen offers the explicit "Unlock to edit" override on a locked class.
 */
export function hasOverridePlacedClassPermission(rolesForOrg) {
  return holdsAnyRole(rolesForOrg, OVERRIDE_PLACED_CLASS_ROLES);
}

/**
 * #2816 — does this user hold score:switch_authoritative_score_sheet for the
 * active org (i.e. may they promote a contender sheet to authoritative)?
 * Same literal-roles-claim predicate as the two checks above — no isSuper
 * bypass on the FE. Drives whether RideEditor's contender-compare dialog
 * offers the "Make authoritative" action at all (AC4: a scribe never sees
 * it, let alone can click it).
 */
export function hasSwitchAuthoritativeScoreSheetPermission(rolesForOrg) {
  return holdsAnyRole(rolesForOrg, SWITCH_AUTHORITATIVE_SCORE_SHEET_ROLES);
}

/**
 * #3009 — does this user hold score:resolve_stale_write_conflict for the
 * active org (i.e. may they resolve a same-field stale_write conflict raised
 * on the review queue)? Same literal-roles-claim predicate as the checks
 * above — no isSuper bypass on the FE. Drives whether ReviewQueue.jsx's
 * stale_write row shows the "Keep current value" / "Apply attempted value"
 * buttons at all — a scribe-only token never even shows them.
 */
export function hasResolveStaleWriteConflictPermission(rolesForOrg) {
  return holdsAnyRole(rolesForOrg, RESOLVE_STALE_WRITE_CONFLICT_ROLES);
}
